import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat.js';
import dayjsCustomParseFormatPlugin from 'dayjs/plugin/customParseFormat.js';
import dayjsLocaleDataPlugin from 'dayjs/plugin/localeData.js';
import dayjsMinMaxPlugin from 'dayjs/plugin/minMax.js';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import dayjsTimezonePlugin from 'dayjs/plugin/timezone.js';
import dayjsUTCPlugin from 'dayjs/plugin/utc.js';
import * as z from 'zod';
import { areShallowEqual } from './arrays.ts';
import {
  formatCalendarDate,
  getWeekStartsOn,
  formatWallTime,
  getInstantCalendarDate,
} from './localization.ts';
import { pluralize } from './strings.ts';
import { parseTimeString } from './time-string.ts';

dayjs.extend(dayjsUTCPlugin);
dayjs.extend(dayjsTimezonePlugin);
dayjs.extend(dayjsCustomParseFormatPlugin);
dayjs.extend(dayjsLocaleDataPlugin);
dayjs.extend(dayjsMinMaxPlugin);
dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);

export const OFFER_EXPIRATION_TIME = '30 days';
export const RESERVATION_REQUEST_EXPIRATION_TIME = '30 days';

const daysOfWeek = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
} as const;

export type Weekdays = keyof typeof daysOfWeek;

/**
 * Day names using JavaScript convention: 0=Sunday, 6=Saturday.
 */
export const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/**
 * Short day names derived from DAY_NAMES (e.g., "Sun", "Mon").
 * Uses JavaScript convention: 0=Sunday, 6=Saturday.
 */
export const DAY_NAMES_SHORT = DAY_NAMES.map(day => day.slice(0, 3)) as readonly string[];

/**
 * Single-letter labels for calendar-style day pickers (0=Sunday … 6=Saturday).
 * Tuesday and Thursday both use "T", matching common S M T W T F S headers.
 */
export const DAY_NAMES_LETTER = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

/**
 * Map day names (multiple formats) to JS day numbers (0=Sunday, 6=Saturday).
 *
 * Includes:
 * - Title case full day names (e.g. "Monday")
 * - Lowercase full day names (e.g. "monday")
 * - 3-letter abbreviations (e.g. "Mon", "mon")
 * - Common variants (e.g. "thurs", "tues")
 */
const dayMap: Record<string, number> = {};
const localeData = dayjs().locale('en').localeData();

localeData.weekdays().forEach((day, index) => {
  dayMap[day] = index;
  dayMap[day.toLowerCase()] = index;
});

localeData.weekdaysShort().forEach((day, index) => {
  dayMap[day] = index;
  dayMap[day.toLowerCase()] = index;
});

export const DAY_NAME_TO_NUMBER: Readonly<Record<string, number>> = {
  ...dayMap,
  tues: 2,
  thur: 4,
  thurs: 4,
};

export const splitDateAndTimeWithoutOffset = (isoString: string) => {
  const date = isoString.split('T')[0];
  const time = isoString.split('T')[1]?.split('-')[0];

  if (!date || !time) {
    throw new Error('Invalid format');
  }

  return { date, time };
};

export const getTimezoneDate = (date: string, time: string, timezone: string, utc = false) => {
  const dayjsDate = dayjs.tz(`${date} ${time}`, timezone);

  return utc ? dayjsDate.utc() : dayjsDate;
};

export const EVENT_WITHIN_TWO_WEEKS_DAYS = 14;

export const getEventDatetimeIso = ({
  date,
  time,
  timezone,
}: {
  date: string | null | undefined;
  time: string | null | undefined;
  timezone: string | null | undefined;
}): string | null => {
  if (!date || !time) {
    return null;
  }

  if (!timezone) {
    return dayjs(`${date} ${time}`).toISOString();
  }

  return getTimezoneDate(date, time, timezone).toISOString();
};

export const isEventWithinTwoWeeks = (datetimeIso: string | null | undefined): boolean => {
  if (!datetimeIso) {
    return false;
  }

  return dayjs(datetimeIso).diff(dayjs(), 'day') < EVENT_WITHIN_TWO_WEEKS_DAYS;
};

export const getUTCDate = (dayjsDate: dayjs.Dayjs) => dayjsDate.utc();

export const convertUTCToTimezone = (date: Date | string | dayjs.Dayjs, timezone: string) =>
  dayjs.utc(date).tz(timezone, true);

export const timezoneSchema = z.custom<string>(timeZone => {
  if (typeof timeZone !== 'string') {
    return false;
  }

  try {
    Intl.DateTimeFormat(undefined, { timeZone });

    return true;
  } catch {
    return false;
  }
}, 'Invalid timezone');

/**
 * Returns a date relative to the initialDateTime. The date will be in the same week as the initialDateTime,
 * but with the weekday set to the input weekday
 */
const getRelativeDayJSByWeekday = (
  weekday: Weekdays,
  time: string,
  initialDateTime?: dayjs.Dayjs,
) => {
  const initial = initialDateTime ?? dayjs();
  const dayNumber = daysOfWeek[weekday];

  const parsedTime = timeStringSchema.safeParse(time);
  if (parsedTime.error) {
    return null;
  }

  const [hour, minute, second] = parsedTime.data;

  const relativeDate = initial
    .day(dayNumber)
    .set('hour', hour)
    .set('minute', minute)
    .set('second', second);
  if (relativeDate.format('dddd') === 'Invalid Date') {
    return null;
  }

  return relativeDate;
};

/**
 * Returns the hour, minute, and seconds components of a time string
 * @param time {String} - A timestring like 05:00 or 15:00:00
 * @returns [hour, minutes, seconds] | null. Hour can be from 0-23, minutes and seconds from 0-59
 */
export const timeStringSchema = z.string().transform((s, ctx) => {
  if (s.length === 0) {
    ctx.addIssue({
      code: 'custom',
      message: 'Cannot convert empty string to time',
    });

    return z.NEVER;
  }

  const parsed = parseTimeString(s);
  if (!parsed) {
    ctx.addIssue({
      code: 'custom',
      message: 'Invalid time string',
    });

    return z.NEVER;
  }

  return parsed;
});

/**
 * Given a target datetime and a set of start/end weekdays and times, will return
 * whether that target datetime is within those ranges (inclusive start, inclusive end)
 *
 * @example: Given a startDay/startTime of FRIDAY 12:00, and endDay/endTime of MONDAY 16:00:
 * - Target datetime of 5/24/2024 (Friday) 11:00 will return false
 * - Target datetime of 5/24/2024 (Friday) 15:00 will return true
 * - Target datetime of 5/27/2024 (Monday) 15:00 will return true
 * - Target datetime of 5/27/2024 (Monday) 16:00 will return true
 * - Target datetime of 5/27/2024 (Monday) 16:01 will return false
 * - Target datetime of 5/28/2024 (Tuesday) 15:00 will return false
 *
 * @example: Given a startDay/startTime of FRIDAY 14:00, and endDay/endTime of FRIDAY 11:00:
 * - all targets return false, since a range cannot start after its end time on the same day
 */
export const isTargetDatetimeInRange = (
  targetDatetime: dayjs.Dayjs,
  {
    startWeekDay,
    startTime,
    endWeekDay,
    endTime,
  }: {
    startWeekDay: Weekdays | null | undefined;
    startTime: string | null | undefined;
    endWeekDay: Weekdays | null | undefined;
    endTime: string | null | undefined;
  },
) => {
  let startDate =
    startWeekDay && startTime
      ? getRelativeDayJSByWeekday(startWeekDay, startTime, targetDatetime)
      : null;
  let endDate =
    endWeekDay && endTime ? getRelativeDayJSByWeekday(endWeekDay, endTime, targetDatetime) : null;

  if (!startDate && !endDate) {
    return false;
  }

  if (!endDate) {
    return !targetDatetime.isBefore(startDate, 'seconds');
  }

  if (!startDate) {
    return !targetDatetime.isAfter(endDate, 'seconds');
  }

  // handle ranges on the same day
  if (startDate.isSame(endDate, 'day')) {
    return (
      !targetDatetime.isBefore(startDate, 'seconds') && !targetDatetime.isAfter(endDate, 'seconds')
    );
  }

  // handle date range wrapping to following week
  if (endDate.day() < startDate.day()) {
    // adjust start/end dates to ensure we compare against the correct week
    if (targetDatetime.isBefore(startDate, 'seconds')) {
      startDate = startDate.subtract(7, 'days');
    } else {
      endDate = endDate.add(7, 'days');
    }
  }

  return (
    !targetDatetime.isBefore(startDate, 'seconds') && !targetDatetime.isAfter(endDate, 'seconds')
  );
};

/**
 * Determines if a given time string is within a range of start and end times
 * @param time {String} - A time string like 05:00 or 15:00:00
 * @param startTime {String} - A time string like 05:00 or 15:00:00
 * @param endTime {String} - A time string like 05:00 or 15:00:00
 * @returns boolean
 */
export const isTimeStringWithinRange = (time: string, startTime: string, endTime: string) => {
  const parsedTime = dayjs(time, ['HH:mm', 'HH:mm:ss']);
  const parsedStartTime = dayjs(startTime, ['HH:mm', 'HH:mm:ss']);
  const parsedEndTime = dayjs(endTime, ['HH:mm', 'HH:mm:ss']);

  if (!parsedTime.isValid() || !parsedStartTime.isValid() || !parsedEndTime.isValid()) {
    return false;
  }

  return parsedTime.unix() >= parsedStartTime.unix() && parsedTime.unix() <= parsedEndTime.unix();
};

const orderedDays = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

const weekdayList = [1, 2, 3, 4, 5];

/**
 * Formats a list of day indexes (0-6) into a human readable string
 * @param days {Array<number>} - A list of day indexes (0-6)
 * @returns string
 */
export const formatDaysToRanges = (days: number[]) => {
  if (days.length === orderedDays.length) {
    return 'Every day';
  }

  if (areShallowEqual(days, weekdayList)) {
    return 'Weekdays';
  }

  const [firstDay, ...rest] = days;
  if (typeof firstDay !== 'number') {
    return '';
  }

  let result = orderedDays[firstDay];
  if (!result) {
    return '';
  }

  let index = firstDay;
  let isInRange = false;

  for (const [dayIndex, day] of rest.entries()) {
    const nextDay = rest[dayIndex + 1];

    if (day === index + 1 && nextDay === index + 2) {
      index++;
      isInRange = true;
      continue;
    }

    index = day;

    if (isInRange) {
      result += ` - ${orderedDays[day]}`;
      isInRange = false;
    } else {
      result += `, ${orderedDays[day]}`;
    }
  }

  return result;
};

export type ParseTimeTo24hOptions = {
  /**
   * If true, allows times without minutes (e.g. `9am` or `9 pm`).
   * When minutes are omitted, `defaultMinutes` is used.
   */
  allowNoMinutes?: boolean;
  /**
   * Minutes to use when parsing times without minutes.
   */
  defaultMinutes?: number;
  /**
   * If true, requires an `am` / `pm` period to be present.
   */
  requirePeriod?: boolean;
  /**
   * If true, supports `noon` -> `12:00` and `midnight` -> `00:00`.
   */
  allowNoonMidnight?: boolean;
};

/**
 * Parses a time string into 24-hour `HH:MM` format.
 *
 * Supports:
 * - `9:00 AM`, `5:30pm`
 * - `9am`, `9 pm` (when `allowNoMinutes`)
 * - `noon`, `midnight` (when `allowNoonMidnight`)
 * - `HH:MM` (24-hour time, when `requirePeriod` is false)
 */
export function parseTimeTo24h(timeStr: string, options?: ParseTimeTo24hOptions): string | null {
  const {
    allowNoMinutes = false,
    defaultMinutes = 0,
    requirePeriod = false,
    allowNoonMidnight = false,
  } = options ?? {};

  const normalized = timeStr.trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  if (allowNoonMidnight) {
    if (normalized === 'noon') {
      return '12:00';
    }
    if (normalized === 'midnight') {
      return '00:00';
    }
  }

  const match = normalized.match(/^(\d{1,2})(?:[:.](\d{2}))?(?:[:.](\d{2}))?\s*(am|pm)?$/i);
  if (!match) {
    return null;
  }

  let hours = Number.parseInt(match[1] ?? '0', 10);
  const minutesStr = match[2];
  const minutes = minutesStr ? Number.parseInt(minutesStr, 10) : defaultMinutes;
  const period = match[4]?.toLowerCase();

  if (!minutesStr && !allowNoMinutes) {
    return null;
  }

  if (requirePeriod && !period) {
    return null;
  }

  if (period === 'pm' && hours !== 12) {
    hours += 12;
  } else if (period === 'am' && hours === 12) {
    hours = 0;
  }

  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/** Minutes since midnight from labels like "7 PM", "7:00 PM", or "19:00". */
export function parseLooseTimeToMinutes(timeLabel: string): number | null {
  const as24h = parseTimeTo24h(timeLabel.trim(), { allowNoMinutes: true });
  if (!as24h) {
    return null;
  }

  const [hourPart, minutePart] = as24h.split(':');
  const hour = Number.parseInt(hourPart ?? '', 10);
  const minute = Number.parseInt(minutePart ?? '', 10);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return null;
  }

  return hour * 60 + minute;
}

export type FormatTimeFrom24hOptions = {
  /**
   * Controls the casing of the meridiem indicator.
   * - `lower`: `am` / `pm`
   * - `upper`: `AM` / `PM`
   */
  periodCase?: 'lower' | 'upper';
  /**
   * If true, drops `:00` minutes (e.g. `3pm` instead of `3:00pm`).
   */
  dropZeroMinutes?: boolean;
  /**
   * If true, omits the space before `am`/`pm` (e.g. `9am`, `2:30pm`) for compact UI.
   */
  omitSpaces?: boolean;
};

/**
 * Formats a 24-hour time string (`HH:MM` or `HH:MM:SS`) to a 12-hour display string.
 *
 * Examples (defaults):
 * - `00:00` -> `12 am`
 * - `09:00` -> `9 am`
 * - `14:30` -> `2:30 pm`
 *
 * With `{ omitSpaces: true }`: `9am`, `2:30pm`.
 */
export function formatTimeFrom24h(time24h: string, options?: FormatTimeFrom24hOptions): string {
  const { periodCase = 'lower', dropZeroMinutes = true, omitSpaces = false } = options ?? {};

  const parts = time24h.split(':');
  const hoursStr = parts[0];
  const minutesStrRaw = parts[1];

  if (!hoursStr || !minutesStrRaw) {
    return time24h;
  }

  const hours = Number.parseInt(hoursStr, 10);
  const minutes = minutesStrRaw.slice(0, 2);

  if (
    Number.isNaN(hours) ||
    hours < 0 ||
    hours > 23 ||
    !/^\d{2}$/.test(minutes) ||
    Number.parseInt(minutes, 10) < 0 ||
    Number.parseInt(minutes, 10) > 59
  ) {
    return time24h;
  }

  const displayHours = hours % 12 || 12;
  const periodBase = hours >= 12 ? 'pm' : 'am';
  const period = periodCase === 'upper' ? periodBase.toUpperCase() : periodBase;

  const spacer = omitSpaces ? '' : ' ';

  if (dropZeroMinutes && minutes === '00') {
    return `${displayHours}${spacer}${period}`;
  }

  return `${displayHours}:${minutes}${spacer}${period}`;
}

/**
 * Formats a 24 hour time ranges into a human readable ranges
 * @param timeRange {string} - A 24 hour time range in the format of HH:mm-HH:mm
 * @returns string
 */
export const formatTimeRange = (timeRange: string) => {
  const [startTime, endTime] = timeRange.split('-');
  const todayDate = dayjs().format('YYYY-MM-DD');

  const startDate = dayjs(`${todayDate}T${startTime}`);
  const endDate = dayjs(`${todayDate}T${endTime}`);

  return `${startDate.format('h:mm a')} - ${endDate.format('h:mm a')}`;
};

export const formatDate = (date: Date | string, locale = 'en-US') => {
  const dateSchema = z.date().transform(dateVal =>
    dateVal.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }),
  );

  const parsedDate = dateSchema.safeParse(new Date(date));

  return parsedDate.success ? parsedDate.data : null;
};

export const earliestDate = dayjs.min;
export const latestDate = dayjs.max;

export const TIMESTAMP_FORMAT = 'YYYY-MM-DD HH:mm:ss.SSS' as const;

export const formatDateWithTimestamp = (dateString: string | null | undefined): string | null => {
  if (!dateString) {
    return null;
  }

  const parsedDate = dayjs(dateString);
  if (!parsedDate.isValid()) {
    return null;
  }

  const isDateOnly = parsedDate.format('YYYY-MM-DD') === dateString && !dateString.includes(' ');

  const now = dayjs();
  const date = parsedDate.format('YYYY-MM-DD');
  const time = isDateOnly ? now.format('HH:mm:ss') : parsedDate.format('HH:mm:ss');
  const milliseconds = String(now.millisecond()).padStart(3, '0');

  return `${date} ${time}.${milliseconds}`;
};

type FormatRelativeTimeParams = {
  expiryDate: Date | string | dayjs.Dayjs;
  withoutSuffix?: boolean;
  targetDate?: dayjs.Dayjs;
};
/**
 * Formats a relative time duration with special handling for sub-hour periods
 * @param expiryDate The target date to compare against now
 * @param withoutSuffix If true, removes the "ago" or "in" suffix
 * @param targetDate The date to compare against
 */
export function formatRelativeTime({
  expiryDate,
  withoutSuffix = true,
  targetDate = dayjs(),
}: FormatRelativeTimeParams): string {
  const target = dayjs(expiryDate);
  const minutesDiff = Math.abs(target.diff(targetDate, 'minutes'));

  // Handle less than 1 hour cases
  if (minutesDiff < 60) {
    return `${minutesDiff} ${pluralize(minutesDiff, 'minute')}`;
  }

  return targetDate.from(target, withoutSuffix);
}

/**
 * Formats a date as a minimal relative time string (e.g., "9h ago", "2d ago")
 * Designed for compact UI elements like notification lists
 * @param date The date to format
 * @param targetDate The date to compare against (defaults to now)
 * @returns A minimal relative time string
 */
export function formatMinimalRelativeTime(
  date: Date | dayjs.Dayjs | string,
  targetDate: dayjs.Dayjs = dayjs(),
): string {
  const dayjsDate = dayjs(date);

  if (dayjsDate.isAfter(targetDate)) {
    return 'now';
  }

  const diffInSeconds = targetDate.diff(dayjsDate, 'second');
  if (diffInSeconds < 60) {
    return `${diffInSeconds}s ago`;
  }

  const diffInMinutes = targetDate.diff(dayjsDate, 'minute');
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  const diffInHours = targetDate.diff(dayjsDate, 'hour');
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }

  const diffInDays = targetDate.diff(dayjsDate, 'day');
  if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  }

  const diffInWeeks = targetDate.diff(dayjsDate, 'week');
  const diffInMonths = targetDate.diff(dayjsDate, 'month');
  if (diffInWeeks < 4 || (diffInWeeks === 4 && diffInMonths === 0)) {
    return `${diffInWeeks}w ago`;
  }

  if (diffInMonths < 12) {
    return `${diffInMonths}mo ago`;
  }

  const diffInYears = targetDate.diff(dayjsDate, 'year');

  return `${diffInYears === 0 ? 1 : diffInYears}y ago`;
}

/**
 * Adds business days to a date, skipping weekends.
 * For example: Friday + 2 business days = Tuesday (skipping Sat/Sun)
 */

type OffsetBusinessDaysParams = {
  date: Date | dayjs.Dayjs;
  days: number;
  offsetType: 'add' | 'subtract';
};

export function offsetBusinessDays({
  date,
  days,
  offsetType,
}: OffsetBusinessDaysParams): dayjs.Dayjs {
  let result = dayjs(date);
  let offsetDays = days;

  while (offsetDays > 0) {
    result = offsetType === 'add' ? result.add(1, 'day') : result.subtract(1, 'day');
    const dayOfWeek = result.day();

    // If it's not a weekend (Saturday = 6, Sunday = 0), count it as a business day
    if (dayOfWeek !== daysOfWeek.SATURDAY && dayOfWeek !== daysOfWeek.SUNDAY) {
      offsetDays--;
    }
  }

  return result;
}

export const businessDaysBetween = (
  startDate: Date | string | dayjs.Dayjs,
  endDate: Date | string | dayjs.Dayjs,
  format: 'days' | 'hours' = 'days',
): number => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const SUNDAY = 0;
  const SATURDAY = 6;

  let businessDays = 0;
  let current = start;

  while (!current.isSame(end, 'day') && !current.isAfter(end)) {
    const dayOfWeek = current.day();
    if (dayOfWeek !== SUNDAY && dayOfWeek !== SATURDAY) {
      businessDays++;
    }
    current = current.add(1, 'day');
  }

  return format === 'days' ? businessDays : businessDays * 24;
};

/**
 * Formats a duration as business time (excluding weekends).
 * Handles hours and minutes appropriately.
 */
export function formatBusinessDayDuration(hours: number): string {
  if (hours < 24) {
    return `${hours} ${pluralize(hours, 'hour')}`;
  }

  const businessDays = Math.round(hours / 24);

  return `${businessDays} business ${pluralize(businessDays, 'day')}`;
}

/**
 * Calculates the number of business days between two dates (Mon-Fri)
 * @param startDate - The start date
 * @param endDate - The end date
 * @returns Number of business days (integer)
 */
export function getBusinessDaysBetween(
  startDate: Date | string | dayjs.Dayjs,
  endDate: Date | string | dayjs.Dayjs,
): number {
  const start = dayjs(startDate).startOf('day');
  const end = dayjs(endDate).startOf('day');
  const SUNDAY = 0;
  const SATURDAY = 6;

  let businessDays = 0;
  let current = start;

  while (current.isBefore(end) || current.isSame(end, 'day')) {
    const dayOfWeek = current.day();
    if (dayOfWeek !== SUNDAY && dayOfWeek !== SATURDAY) {
      businessDays++;
    }
    current = current.add(1, 'day');
  }

  return businessDays;
}

export const getTimesFromTimeString = (timeString: string) => {
  const parsedTime = timeStringSchema.safeParse(timeString);

  if (parsedTime.error) {
    return null;
  }

  return { hours: parsedTime.data[0], minutes: parsedTime.data[1], seconds: parsedTime.data[2] };
};

export const openHoursSlotSchema = z.object({
  dayOfWeekOpen: z.number(),
  openTime: z.string(),
  dayOfWeekClose: z.number(),
  closeTime: z.string(),
});

export type OpenHoursSlot = z.infer<typeof openHoursSlotSchema>;
type OpenHoursSlotConstraint = OpenHoursSlot;

/**
 * Groups opening hour slots by day of the week (0=Sunday, 6=Saturday).
 * Returns a Record where key is day index (0-6) and value is array of slots starting on that day.
 * Ensures all 7 days are present in the returned object, even if empty.
 */
export const groupOpenHoursByDay = <T extends OpenHoursSlotConstraint>(
  openHours: T[] | null | undefined,
): Record<number, T[]> => {
  const result: Record<number, T[]> = {};
  for (let i = 0; i < 7; i++) {
    result[i] = [];
  }

  if (!openHours) {
    return result;
  }

  for (const slot of openHours) {
    const list = result[slot.dayOfWeekOpen];
    if (list) {
      list.push(slot);
    }
  }

  return result;
};

export type GroupedOpeningHourRow = {
  key: string;
  dayLabel: string;
  hoursText: string;
};

const slotSignatureForGroup = (slots: OpenHoursSlot[]) => {
  if (slots.length === 0) {
    return '';
  }

  return slots
    .map(slot => {
      if (slot.dayOfWeekOpen === slot.dayOfWeekClose) {
        return `S:${slot.openTime}:${slot.closeTime}`;
      }

      return `O:${slot.openTime}:${slot.closeTime}:${slot.dayOfWeekClose}`;
    })
    .sort()
    .join('|');
};

const formatOpenHoursDayRangeLabel = (
  start: number,
  end: number,
  shortWeekdays: readonly string[],
) => {
  if (start === end) {
    return shortWeekdays[start] ?? '';
  }

  return `${shortWeekdays[start]} \u2013 ${shortWeekdays[end]}`;
};

const WEEKDAY_ANCHOR_DATE = '2000-01-02';

const buildShortWeekdayNames = (locale: string): readonly string[] =>
  Array.from({ length: 7 }, (_, day) =>
    formatCalendarDate(
      `${WEEKDAY_ANCHOR_DATE.slice(0, 8)}${String(Number(WEEKDAY_ANCHOR_DATE.slice(8)) + day).padStart(2, '0')}`,
      { locale, options: { weekday: 'short' } },
    ),
  );

export const buildGroupedOpeningHourRows = (
  hoursByDay: Record<number, OpenHoursSlot[]>,
  locale: string,
): GroupedOpeningHourRow[] => {
  const shortWeekdays = buildShortWeekdayNames(locale);
  const weekStartsOn = getWeekStartsOn(locale);
  const rows: GroupedOpeningHourRow[] = [];
  let i = 0;
  while (i < 7) {
    const startDay = (weekStartsOn + i) % 7;
    const slots = hoursByDay[startDay] ?? [];
    const sig = slotSignatureForGroup(slots);
    let j = i + 1;
    while (j < 7 && slotSignatureForGroup(hoursByDay[(weekStartsOn + j) % 7] ?? []) === sig) {
      j += 1;
    }
    const endDay = (weekStartsOn + (j - 1)) % 7;
    rows.push({
      key: `${startDay}-${endDay}`,
      dayLabel: formatOpenHoursDayRangeLabel(startDay, endDay, shortWeekdays),
      hoursText:
        slots.length === 0
          ? 'Closed'
          : slots
              .map(
                slot =>
                  `${formatWallTime(slot.openTime, { locale })} \u2013 ${formatWallTime(slot.closeTime, { locale })}`,
              )
              .join('; '),
    });
    i = j;
  }

  return rows;
};

const WEEK_DAYS = 7;
const MIN_GRID_ROWS = 5;
const MAX_GRID_ROWS = 6;
const FIVE_ROW_THRESHOLD = MIN_GRID_ROWS * WEEK_DAYS;

export type CalendarGridRange = { start: dayjs.Dayjs; end: dayjs.Dayjs };

export const getCalendarGridRange = (
  year: number,
  monthOneBased: number,
  weekStartsOn: 0 | 1,
): CalendarGridRange => {
  const firstOfMonth = dayjs(new Date(year, monthOneBased - 1, 1));
  const startDayOfWeek = (firstOfMonth.day() - weekStartsOn + WEEK_DAYS) % WEEK_DAYS;
  const daysInMonth = firstOfMonth.daysInMonth();
  const totalCells = startDayOfWeek + daysInMonth;
  const rows = totalCells > FIVE_ROW_THRESHOLD ? MAX_GRID_ROWS : MIN_GRID_ROWS;
  const cellCount = rows * WEEK_DAYS;
  const start = firstOfMonth.subtract(startDayOfWeek, 'day').startOf('day');
  const lastDay = firstOfMonth.add(daysInMonth - 1, 'day');
  const remaining = cellCount - (startDayOfWeek + daysInMonth);
  const end = lastDay.add(remaining, 'day').startOf('day');

  return { start, end };
};

export const normalizeHoursText = (text: string) => text.trim().toLowerCase().replace(/\s+/g, ' ');

export const isOpen24Hours = (text: string) => {
  const normalized = normalizeHoursText(text);

  return (
    normalized === 'open 24 hours' ||
    normalized === 'open 24 hour' ||
    normalized === 'open 24 hrs' ||
    normalized === 'open 24 hr' ||
    normalized === 'open 24/7'
  );
};

export const TIME_24H_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

const SATURDAY = 6;
const SUNDAY = 0;

export function slideToBusinessDay(date: Date, tz: string): Date {
  const local = dayjs(date).tz(tz);
  if (local.day() === SATURDAY) {
    return local.add(2, 'day').toDate();
  }
  if (local.day() === SUNDAY) {
    return local.add(1, 'day').toDate();
  }

  return date;
}

// Fallback for a venue row without a usable timezone. The venue agent prompt and availability
// tools and the SMS handlers all date "today" by it, so it is defined once.
export const DEFAULT_VENUE_TIMEZONE = 'America/New_York';

export function isEventDatePast(
  eventDate: string | null | undefined,
  timezone?: string | null,
  now: Date = new Date(),
): boolean {
  const trimmed = eventDate?.trim();
  if (!trimmed) {
    return false;
  }

  const dateOnly = dayjs(trimmed, 'YYYY-MM-DD', true);
  if (!dateOnly.isValid()) {
    return false;
  }

  const tz = timezone?.trim() || DEFAULT_VENUE_TIMEZONE;
  const parsed = dayjs.tz(trimmed, 'YYYY-MM-DD', tz);
  if (!parsed.isValid()) {
    return false;
  }

  const today = dayjs(now).tz(tz).startOf('day');

  return parsed.startOf('day').isBefore(today);
}

export function getTimeOfDayPhrase(
  date: dayjs.Dayjs,
): 'Good morning' | 'Good afternoon' | 'Good evening' {
  const hour = date.hour();

  if (hour >= 5 && hour < 12) {
    return 'Good morning';
  }
  if (hour >= 12 && hour < 17) {
    return 'Good afternoon';
  }

  return 'Good evening';
}

export function buildPersonalizedHomeGreeting(
  firstName: string | null | undefined,
  date: dayjs.Dayjs,
): string {
  const phrase = getTimeOfDayPhrase(date);
  const trimmed = firstName?.trim();

  if (!trimmed) {
    return `${phrase}.`;
  }

  return `${phrase}, ${trimmed}.`;
}

export type PlannedDatePrecision = 'DAY' | 'MONTH' | 'QUARTER';

export type PlannedDateFormatInput = {
  precision: PlannedDatePrecision;
  start: string;
};

export type FormatPlannedDateOptions = {
  missingLabel?: string;
  day?: string;
  month?: string;
  quarter?: string;
};

export const DEFAULT_PLANNED_DATE_FORMATS = {
  day: 'MMM D, YYYY',
  month: 'MMM YYYY',
  quarter: '[Q]Q YYYY',
} as const;

const isPlannedDateFormatInput = (value: unknown): value is PlannedDateFormatInput => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const node = value as Record<string, unknown>;

  return (
    (node.precision === 'DAY' || node.precision === 'MONTH' || node.precision === 'QUARTER') &&
    typeof node.start === 'string'
  );
};

export const formatPlannedDate = (
  value: PlannedDateFormatInput | null | undefined,
  options?: FormatPlannedDateOptions,
): string => {
  const missingLabel = options?.missingLabel ?? 'Date TBD';

  if (!isPlannedDateFormatInput(value)) {
    return missingLabel;
  }

  const anchor = dayjs.utc(value.start);

  if (!anchor.isValid()) {
    return missingLabel;
  }

  switch (value.precision) {
    case 'DAY': {
      return anchor.format(options?.day ?? DEFAULT_PLANNED_DATE_FORMATS.day);
    }
    case 'MONTH': {
      return anchor.format(options?.month ?? DEFAULT_PLANNED_DATE_FORMATS.month);
    }
    case 'QUARTER': {
      return anchor.format(options?.quarter ?? DEFAULT_PLANNED_DATE_FORMATS.quarter);
    }
    default: {
      return missingLabel;
    }
  }
};

export type DateBucketLabel = 'Today' | 'Yesterday' | 'Last 7 Days' | 'Older';

export type DateBucketable = {
  updatedAt: string;
};

export type DateBucket<T extends DateBucketable> = {
  label: DateBucketLabel;
  rows: T[];
};

const DATE_BUCKET_ORDER: DateBucketLabel[] = ['Today', 'Yesterday', 'Last 7 Days', 'Older'];

const resolveBucketTimeZone = (timeZone?: string | null): string =>
  timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

const calendarDayDiff = (laterIsoDate: string, earlierIsoDate: string): number => {
  const msPerDay = 24 * 60 * 60 * 1000;

  return Math.round(
    (Date.parse(`${laterIsoDate}T12:00:00.000Z`) - Date.parse(`${earlierIsoDate}T12:00:00.000Z`)) /
      msPerDay,
  );
};

/**
 * Calendar-day list buckets (Today / Yesterday / Last 7 Days / Older).
 * Pass the viewer's IANA timezone (web: `useLocalization().timeZone`); defaults to
 * `Intl.DateTimeFormat().resolvedOptions().timeZone`. Distinct from
 * {@link formatMinimalRelativeTime}, which uses rolling hour/day windows.
 */
export const getDateBucketLabel = (
  updatedAtIso: string,
  now: Date = new Date(),
  timeZone?: string | null,
): DateBucketLabel => {
  const updatedAt = new Date(updatedAtIso);
  if (Number.isNaN(updatedAt.getTime())) {
    return 'Older';
  }

  const zone = resolveBucketTimeZone(timeZone);
  const nowDay = getInstantCalendarDate(now, { timeZone: zone });
  const updatedDay = getInstantCalendarDate(updatedAt, { timeZone: zone });
  if (!nowDay || !updatedDay) {
    return 'Older';
  }

  const dayDiff = calendarDayDiff(nowDay, updatedDay);

  if (dayDiff <= 0) {
    return 'Today';
  }

  if (dayDiff === 1) {
    return 'Yesterday';
  }

  if (dayDiff < 7) {
    return 'Last 7 Days';
  }

  return 'Older';
};

/**
 * Group items by {@link getDateBucketLabel}. Empty buckets are omitted so list headers
 * only appear when the current page has rows in that window.
 */
export const groupByDateBucket = <T extends DateBucketable>(
  rows: readonly T[],
  now: Date = new Date(),
  timeZone?: string | null,
): Array<DateBucket<T>> => {
  const buckets = new Map<DateBucketLabel, T[]>();

  for (const row of rows) {
    const label = getDateBucketLabel(row.updatedAt, now, timeZone);
    const existing = buckets.get(label) ?? [];
    existing.push(row);
    buckets.set(label, existing);
  }

  const grouped: Array<DateBucket<T>> = [];
  for (const label of DATE_BUCKET_ORDER) {
    const bucketRows = buckets.get(label);
    if (bucketRows && bucketRows.length > 0) {
      grouped.push({ label, rows: bucketRows });
    }
  }

  return grouped;
};
